<script setup>
import { computed, watch, onMounted, ref } from "vue";
import { useRoute } from "vue-router";
import {
  useMapGetter,
  useFunctionGetter,
  useStore,
} from "dashboard/composables/store";
import DealsAPI from "dashboard/api/crm/deals";
import { useUISettings } from "dashboard/composables/useUISettings";

import AccordionItem from "dashboard/components/Accordion/AccordionItem.vue";
import ContactConversations from "./ContactConversations.vue";
import ConversationAction from "./ConversationAction.vue";
import ConversationParticipant from "./ConversationParticipant.vue";
import ContactInfo from "./contact/ContactInfo.vue";
import ContactNotes from "./contact/ContactNotes.vue";
import ContactDeals from "./contact/ContactDeals.vue";
import TasksWidget from "../tasks/components/TasksWidget.vue";
import ContactProducts from "./contact/ContactProducts.vue";
import MercadopagoPanel from "./contact/MercadopagoPanel.vue";
import ConversationInfo from "./ConversationInfo.vue";
import CustomAttributes from "./customAttributes/CustomAttributes.vue";
import Draggable from "vuedraggable";
import MacrosList from "./Macros/List.vue";
import ShopifyOrdersList from "dashboard/components/widgets/conversation/ShopifyOrdersList.vue";
import SidebarActionsHeader from "dashboard/components-next/SidebarActionsHeader.vue";
import LinearIssuesList from "dashboard/components/widgets/conversation/linear/IssuesList.vue";
import LinearSetupCTA from "dashboard/components/widgets/conversation/linear/LinearSetupCTA.vue";
import { FEATURE_FLAGS } from "dashboard/featureFlags";

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
  inboxId: {
    type: Number,
    default: undefined,
  },
});

const {
  updateUISettings,
  isContactSidebarItemOpen,
  conversationSidebarItemsOrder,
  toggleSidebarUIState,
} = useUISettings();

const dragging = ref(false);
const conversationSidebarItems = ref([]);

const currentAccountId = useMapGetter("getCurrentAccountId");

const isFeatureEnabledonAccount = useMapGetter(
  "accounts/isFeatureEnabledonAccount",
);

const shopifyIntegration = useFunctionGetter(
  "integrations/getIntegration",
  "shopify",
);

const isShopifyFeatureEnabled = computed(
  () => shopifyIntegration.value.enabled,
);

const linearIntegration = useFunctionGetter(
  "integrations/getIntegration",
  "linear",
);

const isLinearIntegrationEnabled = computed(
  () => linearIntegration.value?.enabled || false,
);

const isLinearFeatureEnabled = isFeatureEnabledonAccount.value(
  currentAccountId.value,
  FEATURE_FLAGS.LINEAR,
);

const store = useStore();
const route = useRoute();
const currentChat = useMapGetter("getSelectedChat");
const conversationId = computed(() => props.conversationId);

// FORK: deals vinculados a esta conversación. conversationDeal (el primero) habilita
// el enlace "Ver negocio" del header, que abre el deal en el board vía deep-link
// (?deal_id=). conversationDeals (todos) alimenta el selector del panel de MercadoPago.
const conversationDeals = ref([]);
const conversationDeal = computed(() => conversationDeals.value[0] || null);

const fetchConversationDeal = async () => {
  conversationDeals.value = [];
  if (!conversationId.value) return;
  try {
    const { data } = await DealsAPI.get({
      conversationId: conversationId.value,
    });
    conversationDeals.value = data?.deals || [];
  } catch (error) {
    conversationDeals.value = [];
  }
};

watch(conversationId, fetchConversationDeal, { immediate: true });

const viewDealRoute = computed(() => {
  if (!conversationDeal.value) return null;
  return {
    name: "crm_board",
    params: { accountId: route.params.accountId },
    query: {
      pipeline_id: conversationDeal.value.pipeline_id,
      deal_id: conversationDeal.value.id,
    },
  };
});
const conversationMetadataGetter = useMapGetter(
  "conversationMetadata/getConversationMetadata",
);
const currentConversationMetaData = computed(() =>
  conversationMetadataGetter.value(conversationId.value),
);
const conversationAdditionalAttributes = computed(
  () => currentConversationMetaData.value.additional_attributes || {},
);

const channelType = computed(() => currentChat.value.meta?.channel);

const contactGetter = useMapGetter("contacts/getContact");
const contactId = computed(() => currentChat.value.meta?.sender?.id);
const contact = computed(() => contactGetter.value(contactId.value));
const contactAdditionalAttributes = computed(
  () => contact.value.additional_attributes || {},
);

// Convert crm_summary to camelCase for the ContactDeals component
const contactCrmSummary = computed(() => {
  if (!contact.value.crm_summary) return null;

  const summary = contact.value.crm_summary;
  return {
    totalDealsCount: summary.total_deals_count,
    wonDealsCount: summary.won_deals_count,
    activeDealsCount: summary.active_deals_count,
    totalRevenueByCurrency: summary.total_revenue_by_currency?.map((item) => ({
      currency: item.currency,
      amountCents: item.amount_cents,
    })),
    potentialRevenueByCurrency: summary.potential_revenue_by_currency?.map(
      (item) => ({
        currency: item.currency,
        amountCents: item.amount_cents,
      }),
    ),
    machinesPurchased: summary.machines_purchased,
    activeDeals: summary.active_deals?.map((deal) => ({
      id: deal.id,
      title: deal.title,
      stageId: deal.stage_id,
      stageName: deal.stage_name,
      status: deal.status,
      pipelineId: deal.pipeline_id,
      availableStages: deal.available_stages?.map((stage) => ({
        id: stage.id,
        name: stage.name,
        position: stage.position,
      })),
    })),
  };
});

const getContactDetails = () => {
  if (contactId.value) {
    store.dispatch("contacts/show", {
      id: contactId.value,
      includeCrmSummary: true,
    });
  }
};

watch(contactId, (newContactId, prevContactId) => {
  if (newContactId && newContactId !== prevContactId) {
    getContactDetails();
  }
});

const onDragEnd = () => {
  dragging.value = false;
  updateUISettings({
    conversation_sidebar_items_order: conversationSidebarItems.value,
  });
};

const closeContactPanel = () => {
  updateUISettings({
    is_contact_sidebar_open: false,
    is_copilot_panel_open: false,
  });
};

onMounted(() => {
  conversationSidebarItems.value = conversationSidebarItemsOrder.value;
  getContactDetails();
  store.dispatch("attributes/get", 0);
  // Load integrations to ensure linear integration state is available
  store.dispatch("integrations/get", "linear");
});
</script>

<template>
  <div class="w-full">
    <SidebarActionsHeader
      :title="$t('CONVERSATION.SIDEBAR.CONTACT')"
      @close="closeContactPanel"
    >
      <template #actions>
        <!-- FORK: enlace al negocio vinculado a esta conversación (espejo de "Ver conversación") -->
        <router-link
          v-if="viewDealRoute"
          :to="viewDealRoute"
          class="inline-flex items-center gap-1 px-3 py-1 bg-n-brand/10 hover:bg-n-brand/20 text-n-brand rounded-full text-xs font-medium transition-colors no-underline whitespace-nowrap"
        >
          {{ $t("CONTACT_PANEL.CRM.VIEW_DEAL") }}
        </router-link>
      </template>
    </SidebarActionsHeader>
    <ContactInfo :contact="contact" :channel-type="channelType" />
    <div class="pb-8 list-group px-2">
      <Draggable
        :list="conversationSidebarItems"
        animation="200"
        ghost-class="ghost"
        handle=".drag-handle"
        item-key="name"
        class="flex flex-col gap-3"
        @start="dragging = true"
        @end="onDragEnd"
      >
        <template #item="{ element }">
          <div
            v-if="element.name === 'conversation_actions'"
            class="conversation--actions"
          >
            <AccordionItem
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.CONVERSATION_ACTIONS')"
              :is-open="isContactSidebarItemOpen('is_conv_actions_open')"
              @toggle="
                (value) => toggleSidebarUIState('is_conv_actions_open', value)
              "
            >
              <ConversationAction
                :conversation-id="conversationId"
                :inbox-id="inboxId"
              />
            </AccordionItem>
          </div>
          <div
            v-else-if="element.name === 'conversation_participants'"
            class="conversation--actions"
          >
            <AccordionItem
              :title="$t('CONVERSATION_PARTICIPANTS.SIDEBAR_TITLE')"
              :is-open="isContactSidebarItemOpen('is_conv_participants_open')"
              @toggle="
                (value) =>
                  toggleSidebarUIState('is_conv_participants_open', value)
              "
            >
              <ConversationParticipant
                :conversation-id="conversationId"
                :inbox-id="inboxId"
              />
            </AccordionItem>
          </div>
          <div v-else-if="element.name === 'conversation_info'">
            <AccordionItem
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.CONVERSATION_INFO')"
              :is-open="isContactSidebarItemOpen('is_conv_details_open')"
              compact
              @toggle="
                (value) => toggleSidebarUIState('is_conv_details_open', value)
              "
            >
              <ConversationInfo
                :conversation-attributes="conversationAdditionalAttributes"
                :contact-attributes="contactAdditionalAttributes"
                :assignee="currentChat.meta?.assignee"
                :status="currentChat.status"
                :created-at="currentChat.created_at"
                :last-activity-at="currentChat.last_activity_at"
              />
            </AccordionItem>
          </div>
          <div v-else-if="element.name === 'contact_deals'">
            <AccordionItem
              v-if="contact.id && contactCrmSummary"
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.CONTACT_DEALS')"
              :is-open="isContactSidebarItemOpen('is_contact_deals_open')"
              compact
              @toggle="
                (value) => toggleSidebarUIState('is_contact_deals_open', value)
              "
            >
              <ContactDeals
                :crm-summary="contactCrmSummary"
                :contact-id="contact.id"
              />
            </AccordionItem>
          </div>
          <div v-else-if="element.name === 'contact_products'">
            <AccordionItem
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.CONTACT_PRODUCTS')"
              :is-open="isContactSidebarItemOpen('is_contact_products_open')"
              compact
              @toggle="
                (value) =>
                  toggleSidebarUIState('is_contact_products_open', value)
              "
            >
              <ContactProducts />
            </AccordionItem>
          </div>
          <div v-else-if="element.name === 'mercadopago_payment'">
            <AccordionItem
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.MERCADOPAGO_PAYMENT')"
              :is-open="isContactSidebarItemOpen('is_mercadopago_open')"
              compact
              @toggle="
                (value) => toggleSidebarUIState('is_mercadopago_open', value)
              "
            >
              <MercadopagoPanel
                :conversation-id="conversationId"
                :deals="conversationDeals"
                @deal-updated="fetchConversationDeal"
              />
            </AccordionItem>
          </div>
          <div v-else-if="element.name === 'contact_attributes'">
            <AccordionItem
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.CONTACT_ATTRIBUTES')"
              :is-open="isContactSidebarItemOpen('is_contact_attributes_open')"
              compact
              @toggle="
                (value) =>
                  toggleSidebarUIState('is_contact_attributes_open', value)
              "
            >
              <CustomAttributes
                attribute-type="contact_attribute"
                attribute-from="conversation_contact_panel"
                :contact-id="contact.id"
                :empty-state-message="
                  $t('CONVERSATION_CUSTOM_ATTRIBUTES.NO_RECORDS_FOUND')
                "
              />
            </AccordionItem>
          </div>
          <div v-else-if="element.name === 'previous_conversation'">
            <AccordionItem
              v-if="contact.id"
              :title="
                $t('CONVERSATION_SIDEBAR.ACCORDION.PREVIOUS_CONVERSATION')
              "
              :is-open="isContactSidebarItemOpen('is_previous_conv_open')"
              compact
              @toggle="
                (value) => toggleSidebarUIState('is_previous_conv_open', value)
              "
            >
              <ContactConversations
                :contact-id="contact.id"
                :conversation-id="conversationId"
              />
            </AccordionItem>
          </div>
          <woot-feature-toggle
            v-else-if="element.name === 'macros'"
            feature-key="macros"
          >
            <AccordionItem
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.MACROS')"
              :is-open="isContactSidebarItemOpen('is_macro_open')"
              compact
              @toggle="(value) => toggleSidebarUIState('is_macro_open', value)"
            >
              <MacrosList :conversation-id="conversationId" />
            </AccordionItem>
          </woot-feature-toggle>
          <div
            v-else-if="
              element.name === 'linear_issues' && isLinearFeatureEnabled
            "
          >
            <AccordionItem
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.LINEAR_ISSUES')"
              :is-open="isContactSidebarItemOpen('is_linear_issues_open')"
              compact
              @toggle="
                (value) => toggleSidebarUIState('is_linear_issues_open', value)
              "
            >
              <LinearSetupCTA v-if="!isLinearIntegrationEnabled" />
              <LinearIssuesList v-else :conversation-id="conversationId" />
            </AccordionItem>
          </div>
          <div
            v-else-if="
              element.name === 'shopify_orders' && isShopifyFeatureEnabled
            "
          >
            <AccordionItem
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.SHOPIFY_ORDERS')"
              :is-open="isContactSidebarItemOpen('is_shopify_orders_open')"
              compact
              @toggle="
                (value) => toggleSidebarUIState('is_shopify_orders_open', value)
              "
            >
              <ShopifyOrdersList :contact-id="contactId" />
            </AccordionItem>
          </div>
          <div v-else-if="element.name === 'contact_notes'">
            <AccordionItem
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.CONTACT_NOTES')"
              :is-open="isContactSidebarItemOpen('is_contact_notes_open')"
              compact
              @toggle="
                (value) => toggleSidebarUIState('is_contact_notes_open', value)
              "
            >
              <ContactNotes :contact-id="contactId" />
            </AccordionItem>
          </div>
          <div v-else-if="element.name === 'contact_activities'">
            <AccordionItem
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.CONTACT_ACTIVITIES')"
              :is-open="isContactSidebarItemOpen('is_contact_activities_open')"
              compact
              @toggle="
                (value) =>
                  toggleSidebarUIState('is_contact_activities_open', value)
              "
            >
              <TasksWidget
                :contact-id="contactId"
                :conversation-id="Number(conversationId)"
              />
            </AccordionItem>
          </div>
        </template>
      </Draggable>
    </div>
  </div>
</template>

<style lang="scss" scoped>
::v-deep {
  .contact--profile {
    @apply pb-3 border-b border-solid border-n-weak;
  }

  .conversation--actions .multiselect-wrap--small {
    .multiselect {
      @apply box-border pl-6;
    }

    .multiselect__element {
      span {
        @apply w-full;
      }
    }
  }
}
</style>
